/**
 * @description Component-AddLiqForm
 */
import './index.less';

import { useDebounceEffect } from 'ahooks';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormInput from '@/components/FormInput';
import { FETCHING_STATUS } from '@/constants';
import { EARN_TYPE } from '@/constants/earn';
import { EARN_ALPHA_THRESHOLDS } from '@/constants/global';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useMainPosition } from '@/features/account/positionHook';
import { useDisplayBalance } from '@/features/balance/hook';
import { resetLiquidityFormByChainId, setAddLiqFormState, setEarnFormType } from '@/features/earn/action';
import {
  useAddLiquidationSimulation,
  useAddLiquidityFormState,
  useAddLiquidityFormStateStatus,
} from '@/features/earn/hook';
import { useCombinedPairFromUrl } from '@/features/pair/hook';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';

import { AvailableBalancePosition } from '@/components/AvailableBalancePosition';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import EarnFormFooter from '../../EarnFormFooter';
import AddLiqFormDetail from '../AddLiqFormDetail';
import EarnFormAlert from '../EarnFormAlert';
import PriceRangeAndChart from './PriceRangeAndChart';
interface IPropTypes {
  className?: string;
  onClose?: () => void;
}
const AddLiqForm: FC<IPropTypes> = function ({ onClose }) {
  const chainId = useChainId();
  const currentPair = useCombinedPairFromUrl(chainId);
  const userAddr = useUserAddr();
  const { t } = useTranslation();
  const { deviceType } = useMediaQueryDevice();
  const addLiqState = useAddLiquidityFormState(chainId);
  const addLiqStatus = useAddLiquidityFormStateStatus(chainId);
  const dispatch = useAppDispatch();
  const simulation = useAddLiquidationSimulation(chainId);
  const currentImr = useMemo(() => {
    return currentPair?.rootInstrument.setting.initialMarginRatio || EARN_ALPHA_THRESHOLDS.DEFAULT_IMR;
  }, [currentPair?.rootInstrument?.setting?.initialMarginRatio]);
  const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const displaceBalance = useDisplayBalance(
    currentPosition,
    true,
    chainId,
    currentPair?.rootInstrument.quoteToken.address,
  );
  const inputAmountStrChanged = useCallback(
    (inputAmountStr: string) => {
      inputAmountStr &&
        gtag('event', 'enter_liquidity_size', {
          enter_action: 'enter',
        });
      addLiqState &&
        dispatch(
          setAddLiqFormState({
            ...addLiqState,
            amount: inputAmountStr,
          }),
        );
    },
    [dispatch, addLiqState],
  );

  const onClickMax = useCallback(
    (balance: WrappedBigNumber) => {
      inputAmountStrChanged(balance.stringValue);
    },
    [inputAmountStrChanged],
  );

  const onNativeTokenDepositClick = useCallback(() => {
    chainId &&
      dispatch(
        setEarnFormType({
          chainId,
          formType: EARN_TYPE.DEPOSIT_NATIVE,
        }),
      );
  }, [chainId, dispatch]);
  useDebounceEffect(() => {
    document.getElementById('earnAmount')?.focus();
  }, []);

  useEffect(() => {
    if (currentPair?.id) {
      dispatch(resetLiquidityFormByChainId({ chainId: currentPair.chainId }));
    }
  }, [currentPair?.id]);

  return (
    <div className={classNames('syn-add-liq-form', deviceType)}>
      <PriceRangeAndChart />
      <div className="syn-add-liq-form-content-section">
        <div className="syn-add-liq-form-content-title">{t('common.formInput.placeHolder')}</div>

        <FormInput
          inputProps={{
            type: 'text',
            pattern: '^[0-9]*[.,]?[0-9]*$',
            inputMode: 'decimal',
            autoComplete: 'off',
            id: 'earnAmount',
            autoCorrect: 'off',
            onKeyDown: (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
            step: 1e18,
          }}
          max={displaceBalance.stringValue}
          disabled={addLiqStatus === FETCHING_STATUS.FETCHING}
          className="syn-add-liq-form-content-input"
          inputAmountStr={addLiqState?.amount || ''}
          inputAmountStrChanged={inputAmountStrChanged}
          suffix={currentPair?.rootInstrument.quoteToken?.symbol}
        />
      </div>
      <AvailableBalancePosition showPosition={false} currentPair={currentPair} />

      <EarnFormFooter
        isMobile={true}
        inputAmountStr={addLiqState?.amount}
        earnType={EARN_TYPE.ADD_LIQ}
        onClose={onClose}
        currentPair={currentPair}
      />

      {WrappedBigNumber.from(addLiqState?.amount || 0).notEq(0) && (
        <>
          {
            <>
              <AddLiqFormDetail imr={currentImr} currentPair={currentPair} />
              <EarnFormAlert
                simulation={simulation}
                chainId={chainId}
                earnType={EARN_TYPE.ADD_LIQ}
                marginToken={currentPair?.rootInstrument.quoteToken}
                onMaxBalanceClick={(balance) => {
                  onClickMax(balance);
                }}
                onNativeTokenDepositClick={onNativeTokenDepositClick}
              />
            </>
          }
        </>
      )}
    </div>
  );
};

export default AddLiqForm;
